<template>
    <div class="country-mx" :class="$x.nsAddressForm">
        <div :class="$x.dualColumn">
            <div class="flex-column" :class="$x.col">
                <VTextField
                    v-model="address.postalCode"
                    :label="t.postalCodeLabel"
                    name="postalCode"
                    maxlength="5"
                    required
                    validate-on-blur
                    :rules="postalCodeRules"
                    :hide-details="false"
                    :class="$x.postCodeMessage"
                    :error-messages="postCodeErrors"
                    @blur="onBlur"
                    @input="postCodeChanged"
                >
                </VTextField>
                <span :class="$x.postalCodeHint" v-html="sanitizeHTMLCode(t.postCodeDescription)"></span>
            </div>
            <VTextField
                ref="addressRegion"
                v-model="address.region"
                name="region"
                maxlength="4"
                required
                validate-on-blur
                :label="t.regionLabel"
                class="flex-column"
                :class="$x.col"
                :rules="cityRules"
                :readonly="postCodeLookedUp"
                @input="addressUpdated"
            ></VTextField>
        </div>
        <VTextField
            ref="addressCity"
            v-model="address.city"
            name="city"
            class="flex-column"
            maxlength="30"
            required
            validate-on-blur
            :label="t.cityLabel"
            :rules="cityRules"
            :readonly="postCodeLookedUp"
            @blur="onBlur"
            @input="addressUpdated"
        />
        <NsSelect
            v-model="address.district"
            :items="districts"
            name="district"
            class="flex-column"
            :label="t.districtLabel"
            :rules="requiredRule"
            @blur="onBlur"
            @input="addressUpdated"
        ></NsSelect>
        <VTextField
            v-model="address.address1"
            name="address1"
            class="flex-column"
            maxlength="50"
            required
            validate-on-blur
            :label="t.address1Label"
            :rules="address1Rules"
            @blur="onBlur"
            @input="addressUpdated"
        />
        <VTextField
            v-model="address.address2"
            name="address2"
            class="flex-column"
            maxlength="50"
            validate-on-blur
            :aria-label="t.address2Label"
            :rules="address2Rules"
            @blur="onBlur"
            @input="addressUpdated"
        >
            <template #label>
                {{ t.address2Label }}
                <span :class="$x.optional"> {{ t.optional }} </span>
            </template>
        </VTextField>
        <VTextField
            v-if="includeEmail"
            v-model="address.email"
            name="email"
            class="flex-column"
            maxlength="50"
            required
            validate-on-blur
            :label="t.emailLabel"
            :rules="emailRules"
            @input="addressUpdated"
            @blur="onBlur"
        />
        <NsModal
            v-model="modalOpen"
            attach
            context-class="addressModal"
            :title="t.addressSelect"
        >
            <VLayout class="mb-4">
                {{ t.addressVerificationContext }}
            </VLayout>
            <VDivider></VDivider>

            <VForm ref="addressSelectForm" class="font-weight-regular">
                <VRadioGroup class="pt-4 pl-4">
                    <VLayout class="mb-3 font-weight-bold">
                        {{ t.original }}
                    </VLayout>
                    <VRadio
                        :key="address.address1"
                        :v-model="selectedAddress"
                        :value="address"
                        color="#7123d9"
                        @change="addressSelected"
                    >
                        <template slot="label">
                            <VLayout class="font-weight-regular">
                                <span v-html="sanitizeHTMLCode(formatAddress(address))"></span>
                            </VLayout>
                        </template>
                    </VRadio>

                    <VLayout class="mt-3 font-weight-bold">
                        {{ t.suggested }}
                    </VLayout>
                    <VLayout
                        v-if="
                            suggestedAddressesValue && suggestedAddressesValue.length == 0
                        "
                        class="mt-3"
                    >
                        {{ t.noSuggestionsFound }}
                    </VLayout>
                    <template
                        v-if="suggestedAddressesValue && suggestedAddressesValue.length > 0"
                    >
                        <VRadio
                            v-for="item in suggestedAddressesValue"
                            :key="item.address1"
                            class="mt-3"
                            :v-model="selectedAddress"
                            :value="item"
                            color="#7123d9"
                            @change="addressSelected"
                        >
                            <template slot="label">
                                <VLayout class="font-weight-regular">
                                    <span v-html="sanitizeHTMLCode(formatAddress(item, true))"></span>
                                </VLayout>
                            </template>
                        </VRadio>
                    </template>
                </VRadioGroup>
            </VForm>
            <template>
                <VLayout align-center justify-center>
                    <VLayout sm="3" xs="12">
                        <button
                            flat
                            outline
                            block
                            :disabled="Object.entries(selectedAddress).length === 0"
                            class="primary-hit fluid"
                            style="margin: 5px"
                            @click="
                                () => {
                                    addressConfirmed(selectedAddress);
                                    selectedAddress = {};
                                }
                            "
                        >
                            {{ t.confirmAddress }}
                        </button>
                        <button
                            flat
                            outline
                            block
                            class="secondary-hit fluid"
                            style="margin: 5px"
                            @click.prevent.stop="
                                () => {
                                    addressClose();
                                    selectedAddress = {};
                                }
                            "
                        >
                            {{ t.cancel }}
                        </button>
                    </VLayout>
                </VLayout>
            </template>
        </NsModal>
    </div>
</template>

<script>
import {
    VDivider,
    VForm,
    VLayout,
    VRadio,
    VRadioGroup,
    VTextField,
} from 'vuetify/lib';
import {NsSelect, NsModal} from '@nuskin/design-components';
import ADDRESS_MIXIN from '../addressMixin';
import POST_CODE_LOOKUP from '../postalCodeLookup';
import REGIONS from './regions';
// Most of the logic for this component is in the mixin
import TRANSLATIONS from './translations';

export default {
    name: 'NsAddressMx',
    components: {
        VDivider,
        VForm,
        VLayout,
        VRadio,
        VRadioGroup,
        VTextField,
        NsSelect,
        NsModal,
    },
    mixins: [ADDRESS_MIXIN, POST_CODE_LOOKUP],
    props: {
        suggestedAddressesValue: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            districts: [],
            regions: REGIONS,
            selectedAddress: {},
            postCodeErrors: [],
        };
    },
    watch: {
        selectedAddress() {
            if (
                this.selectedAddress &&
        Object.entries(this.selectedAddress) &&
        Object.entries(this.selectedAddress).length === 0
            ) {
                this.$refs.addressSelectForm.reset();
            } else {
                this.updateDistricts();
            }
        },
    },
    async mounted() {
        this.address.country = 'MX';
        this.postalCodeResetRefs = [
            this.$refs.addressCity,
            this.$refs.addressRegion,
        ];

        if (TRANSLATIONS[this.lang]) {
            this.t = TRANSLATIONS[this.lang];
        } else {
            // If the language isn't available then get the first language in translations file
            this.t = TRANSLATIONS[Object.keys(TRANSLATIONS)[0]];
        }
        // If we have a postalCode on load then we need to do postalCodeLookup
        if (this.address.postalCode) {
            await this.postCodeChanged();
            this.updateDistricts();
        }
        this.$emit('new-address-in-form');
    },
    methods: {
        addressSelected(address) {
            this.selectedAddress = address;
        },

        formatAddress(address, isSuggested = false) {
            const bottomLine = [address.postalCode, address.city, address.region]
                .filter((n) => n)
                .join(', ');

            if (isSuggested) {
                return [address.address1, address.district, bottomLine]
                    .filter((n) => n)
                    .join('<br />');
            }

            return [address.address1, address.address2, address.district, bottomLine]
                .filter((n) => n)
                .join('<br />');
        },

        // Make sure the address district is in the list of districts
        updateDistricts() {
            if (
                !this.districts.some((district) => district === this.address.district)
            ) {
                this.districts.push(this.address.district);
                this.districts = this.districts.sort();
            }
        },
    },
};
</script>

<style lang="scss" module="$x">
@use "sass:map";
@use '~@nuskin/ns-core-styles/src/scss/buttons';
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/global.mixins";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";

$color-eeyore: map.get(colors.$baseColors, eyore);

.dualColumn {
  display: flex;
  flex-wrap: wrap;
  align-content: space-between;
  margin-left: -10px;
  margin-right: -10px;

  .col {
    flex-basis: 50%;
    padding-left: 10px;
    padding-right: 10px;

    @media (max-width: 479px) {
      flex-basis: 100%;
    }
  }
}

.postCodeMessage {
  :global .v-messages {
    min-height: 0;
    padding-bottom: 0;
  }
}
.postalCodeHint {
  font-size: 0.75em;
}

.optional {
  color: $color-eeyore;
}
</style>
